import { DollarSign, ShoppingCart, Users, CreditCard, TrendingUp, Building, Package, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '../ui/button';

export default function DashboardTab({ 
  stats, 
  projections, 
  companyAnalytics, 
  topProducts, 
  inventory,
  onRefresh 
}) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-stone-900">Dashboard</h1>
        <Button variant="outline" size="sm" className="rounded-full" onClick={onRefresh}>
          <RefreshCw className="w-4 h-4 mr-2" />
          Actualizar
        </Button>
      </div>
      
      {/* Stats Grid */}
      {stats && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                <DollarSign className="w-5 h-5 text-emerald-700" />
              </div>
            </div>
            <p className="text-2xl font-bold text-stone-900">${stats.total_revenue?.toLocaleString() || 0}</p>
            <p className="text-sm text-stone-500">Ingresos Totales</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-blue-100 rounded-xl flex items-center justify-center">
                <ShoppingCart className="w-5 h-5 text-blue-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-stone-900">{stats.paid_orders || 0}</p>
            <p className="text-sm text-stone-500">Pedidos Pagados</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-orange-100 rounded-xl flex items-center justify-center">
                <Users className="w-5 h-5 text-orange-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-stone-900">{stats.total_users || 0}</p>
            <p className="text-sm text-stone-500">Usuarios</p>
          </div>
          <div className="bg-white rounded-2xl p-6 border border-stone-200">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
                <CreditCard className="w-5 h-5 text-purple-600" />
              </div>
            </div>
            <p className="text-2xl font-bold text-stone-900">{stats.total_subscriptions || 0}</p>
            <p className="text-sm text-stone-500">Suscripciones</p>
          </div>
        </div>
      )}

      {/* Projections */}
      {projections && (
        <div className="bg-gradient-to-r from-emerald-600 to-emerald-700 rounded-2xl p-6 text-white">
          <h3 className="font-semibold mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Proyecciones de Ventas
          </h3>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <p className="text-emerald-200 text-sm">Promedio Diario</p>
              <p className="text-2xl font-bold">${projections.daily_avg?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-emerald-200 text-sm">Proyección Semanal</p>
              <p className="text-2xl font-bold">${projections.weekly_projection?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-emerald-200 text-sm">Proyección Mensual</p>
              <p className="text-2xl font-bold">${projections.monthly_projection?.toLocaleString()}</p>
            </div>
            <div>
              <p className="text-emerald-200 text-sm">Proyección Anual</p>
              <p className="text-2xl font-bold">${projections.yearly_projection?.toLocaleString()}</p>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats */}
      <div className="grid lg:grid-cols-2 gap-6">
        {/* Top Companies */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Building className="w-5 h-5 text-emerald-700" />
            Top Empresas
          </h3>
          <div className="space-y-3">
            {companyAnalytics.slice(0, 5).map((company) => (
              <div key={company.company || company._id} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-stone-900">{company.company}</p>
                  <p className="text-sm text-stone-500">{company.total_orders} pedidos</p>
                </div>
                <p className="font-bold text-emerald-700">${company.total_sales?.toLocaleString()}</p>
              </div>
            ))}
            {companyAnalytics.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-4">Sin datos</p>
            )}
          </div>
        </div>

        {/* Top Products */}
        <div className="bg-white rounded-2xl border border-stone-200 p-6">
          <h3 className="font-semibold text-stone-900 mb-4 flex items-center gap-2">
            <Package className="w-5 h-5 text-emerald-700" />
            Productos Más Vendidos
          </h3>
          <div className="space-y-3">
            {topProducts.slice(0, 5).map((product) => (
              <div key={product.product_id || product.name} className="flex items-center justify-between">
                <div>
                  <p className="font-medium text-stone-900">{product.name}</p>
                  <p className="text-sm text-stone-500">{product.quantity_sold} unidades</p>
                </div>
                <p className="font-bold text-emerald-700">${product.total_revenue?.toLocaleString()}</p>
              </div>
            ))}
            {topProducts.length === 0 && (
              <p className="text-sm text-stone-400 text-center py-4">Sin datos</p>
            )}
          </div>
        </div>
      </div>

      {/* Inventory Alerts */}
      {inventory.summary?.low_stock_count > 0 && (
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-6">
          <h3 className="font-semibold text-orange-800 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-5 h-5" />
            Alertas de Inventario
          </h3>
          <div className="grid md:grid-cols-3 gap-4">
            {inventory.items.filter(i => i.status !== 'ok').slice(0, 6).map((item) => (
              <div key={item.product_id || item.name} className="bg-white rounded-xl p-4 border border-orange-200">
                <p className="font-medium text-stone-900">{item.name}</p>
                <p className="text-sm text-orange-600">Stock: {item.stock} / Mínimo: {item.min_stock}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
